import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react'
import { useGLTF, Edges } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { applyMaterialToModel, applyGradientMaterial, MATERIALS } from './Materials'
import { getIntersectionSegments, buildContours, triangulateContour, updateCrossSectionVisibility, CROSS_SECTION_COLOR } from './CrossSectionComputer'
import * as THREE from 'three'

// Performance: Skip frames for non-critical animations
const FRAME_SKIP = 2 // Update every 2nd frame for particles/gradients

// Global store for shape geometries (similar to reference index.html)
// This allows CrossSectionManager to find shapes without complex scene traversal
window.shapeGeometries = window.shapeGeometries || []

// DEBUG CAGE CONFIG - These can be overridden by debug panel
// User's preferred settings (Jan 2026) - Colors now match reference index.html
export const DEFAULT_DEBUG_CONFIG = {
  cagePosition: { x: 0, y: 45, z: 0 },
  cageSize: { x: 41, y: 50, z: 24 },
  cageColor: '#ffffff',  // White cage wireframe
  shapeScale: 0.7,
  teamAColor: '#4ecdc4',  // Teal/cyan like reference
  teamBColor: '#FFFFC5',  // Light cream/yellow for Team B
  crossSectionColor: '#ff6b35'  // Orange cross-sections like reference
}

// These will be set dynamically based on debugConfig prop
let CAGE_CENTER = DEFAULT_DEBUG_CONFIG.cagePosition
let CAGE_SIZE = DEFAULT_DEBUG_CONFIG.cageSize

// Function to create clipping planes from cage config
// MATCHING REFERENCE index.html EXACTLY - normals point outward from cage center
function createClippingPlanes(center, size) {
  const px = center.x
  const py = center.y
  const pz = center.z
  
  // Plane definitions matching reference exactly
  // Normals point OUTWARD from cage center, constants computed as in reference
  return [
    new THREE.Plane(new THREE.Vector3(-1, 0, 0), size.x / 2 + px),  // Right wall (clips x > px + size.x/2)
    new THREE.Plane(new THREE.Vector3(1, 0, 0), size.x / 2 - px),   // Left wall (clips x < px - size.x/2)
    new THREE.Plane(new THREE.Vector3(0, -1, 0), size.y / 2 + py),  // Top wall (clips y > py + size.y/2)
    new THREE.Plane(new THREE.Vector3(0, 1, 0), size.y / 2 - py),   // Bottom wall (clips y < py - size.y/2)
    new THREE.Plane(new THREE.Vector3(0, 0, -1), size.z / 2 + pz),  // Front wall (clips z > pz + size.z/2)
    new THREE.Plane(new THREE.Vector3(0, 0, 1), size.z / 2 - pz),   // Back wall (clips z < pz - size.z/2)
  ]
}

// Initial clipping planes (will be updated by Scene component)
let clippingPlanes = createClippingPlanes(CAGE_CENTER, CAGE_SIZE)

// Export cage config for GameController
export const CAGE_CONFIG = { center: CAGE_CENTER, size: CAGE_SIZE }

// Simple wireframe cage visualization (like in index.html reference)
// Renders multiple overlapping edges to create thicker appearance (5x thickness)
function WireframeCage({ visible = true, position, size, color = '#ff6b35' }) {
  const edgesRef = useRef()
  
  // Create multiple slightly offset geometries for thickness effect
  const offsets = useMemo(() => {
    const thickness = 0.15  // Offset amount for 5x thickness effect
    return [
      [0, 0, 0],
      [thickness, 0, 0],
      [-thickness, 0, 0],
      [0, thickness, 0],
      [0, -thickness, 0],
      [0, 0, thickness],
      [0, 0, -thickness],
      [thickness, thickness, 0],
      [-thickness, -thickness, 0],
      [thickness, 0, thickness],
      [-thickness, 0, -thickness],
    ]
  }, [])
  
  const geometry = useMemo(() => {
    return new THREE.BoxGeometry(size.x, size.y, size.z)
  }, [size.x, size.y, size.z])
  
  const edgesGeometry = useMemo(() => {
    return new THREE.EdgesGeometry(geometry)
  }, [geometry])
  
  return (
    <group position={[position.x, position.y, position.z]} visible={visible}>
      {offsets.map((offset, i) => (
        <lineSegments 
          key={i}
          position={offset}
        >
          <primitive object={edgesGeometry} attach="geometry" />
          <lineBasicMaterial color={color} linewidth={2} transparent opacity={i === 0 ? 1 : 0.7} />
        </lineSegments>
      ))}
    </group>
  )
}

function applyMicroJitterToGeometry(geometry) {
  if (!geometry) return;
  
  const positionAttribute = geometry.getAttribute('position');
  if (positionAttribute) {
    const positions = positionAttribute.array;
    for (let i = 0; i < positions.length; i += 3) {
      positions[i] += (Math.random() - 0.5) * 0.002;
      positions[i + 1] += (Math.random() - 0.5) * 0.002;
      positions[i + 2] += (Math.random() - 0.5) * 0.002;
    }
    positionAttribute.needsUpdate = true;
  }
}

// ============================================
// CROSS-SECTION MANAGER - OPTIMIZED VERSION
// Only recomputes when shapes change, not every frame
// Uses throttling to prevent excessive computation
// ============================================
function CrossSectionManager({ planes, crossSectionColor, camera }) {
  const { scene } = useThree()
  const crossSectionGroupRef = useRef(new THREE.Group())
  const lastShapeCountRef = useRef(0)
  const lastUpdateTimeRef = useRef(0)
  const UPDATE_INTERVAL = 500 // Only update every 500ms max
  
  // Add cross-section group to scene once
  useEffect(() => {
    scene.add(crossSectionGroupRef.current)
    return () => {
      scene.remove(crossSectionGroupRef.current)
    }
  }, [scene])
  
  // Compute cross-sections with throttling (not every frame)
  useFrame(() => {
    const now = Date.now()
    const group = crossSectionGroupRef.current
    
    // Count current shapes
    let shapeCount = 0
    scene.traverse((child) => {
      if (child.isMesh && child.userData.isClippedShape) {
        shapeCount++
      }
    })
    
    // Only recompute if:
    // 1. Shape count changed (new shape added)
    // 2. Enough time passed since last update
    const shapeCountChanged = shapeCount !== lastShapeCountRef.current
    const timePassed = now - lastUpdateTimeRef.current > UPDATE_INTERVAL
    
    if (!shapeCountChanged && !timePassed) {
      // Just update visibility based on camera (cheap operation)
      for (const mesh of group.children) {
        const planeNormal = mesh.userData.planeNormal
        if (planeNormal) {
          const meshPos = new THREE.Vector3()
          mesh.getWorldPosition(meshPos)
          const toMesh = new THREE.Vector3().subVectors(meshPos, camera.position).normalize()
          mesh.visible = planeNormal.dot(toMesh) > 0
        }
      }
      return
    }
    
    // Update tracking
    lastShapeCountRef.current = shapeCount
    lastUpdateTimeRef.current = now
    
    // Clear old cross-section meshes
    while (group.children.length > 0) {
      const child = group.children[0]
      group.remove(child)
      if (child.geometry) child.geometry.dispose()
      if (child.material) child.material.dispose()
    }
    
    // Create cross-section material (reuse for all meshes)
    const crossSectionMaterial = new THREE.MeshStandardMaterial({
      color: new THREE.Color(crossSectionColor),
      side: THREE.DoubleSide,
      metalness: 0.2,
      roughness: 0.6
    })
    
    // Find all clipped shape meshes
    const allGeometries = []
    
    scene.traverse((child) => {
      if (child.isMesh && child.geometry && child.userData.isClippedShape) {
        child.updateMatrixWorld(true)
        allGeometries.push({
          geometry: child.geometry,
          matrixWorld: child.matrixWorld.clone()
        })
      }
    })
    
    if (allGeometries.length === 0) return
    
    // For each plane, compute cross-sections
    for (let i = 0; i < 6; i++) {
      const plane = planes[i]
      const otherPlanes = planes.filter((_, idx) => idx !== i)
      
      const allSegments = []
      for (const { geometry, matrixWorld } of allGeometries) {
        const segments = getIntersectionSegments(geometry, plane, otherPlanes, matrixWorld)
        allSegments.push(...segments)
      }
      
      if (allSegments.length === 0) continue
      
      const contours = buildContours(allSegments)
      
      for (const contour of contours) {
        const sectionGeometry = triangulateContour(contour, plane)
        if (sectionGeometry) {
          const mesh = new THREE.Mesh(sectionGeometry, crossSectionMaterial.clone())
          mesh.userData.planeIndex = i
          mesh.userData.planeNormal = plane.normal.clone()
          mesh.renderOrder = 20000
          group.add(mesh)
        }
      }
    }
    
    // Update visibility
    for (const mesh of group.children) {
      const planeNormal = mesh.userData.planeNormal
      if (planeNormal) {
        const meshPos = new THREE.Vector3()
        mesh.getWorldPosition(meshPos)
        const toMesh = new THREE.Vector3().subVectors(meshPos, camera.position).normalize()
        mesh.visible = planeNormal.dot(toMesh) > 0
      }
    }
  })
  
  return null
}

// ============================================
// MODEL COMPONENT - NO cross-section computation (handled by manager)
// ============================================
function Model({ url, materialType, useGradient, renderOrder = 0, showOutline = false, outlineColor = '#808080', outlineThickness = 1, outlineAngle = 45, outlineMode = 'edges', useClipping = false, clippingPlanes: propClippingPlanes, teamColor, ...props }) {
  const { scene: gltfScene } = useGLTF(url)
  const meshRef = useRef()
  const frameCount = useRef(0)
  const groupRef = useRef()
  
  // PERFORMANCE FIX: Memoize cloned scene - only clone once per url
  const clonedScene = useMemo(() => {
    const cloned = gltfScene.clone()
    
    cloned.traverse((child) => {
      if (child.isMesh && child.geometry) {
        applyMicroJitterToGeometry(child.geometry)
        
        if (child.material && child.material.clone) {
          const uniqueMaterial = child.material.clone()
          if (!uniqueMaterial.polygonOffset) {
            uniqueMaterial.polygonOffset = true
            uniqueMaterial.polygonOffsetFactor = renderOrder * 0.1 + 1
            uniqueMaterial.polygonOffsetUnits = renderOrder * 0.1 + 1
          }
          child.material = uniqueMaterial
        }
      }
    })
    
    return cloned
  }, [gltfScene, renderOrder])
  
  // Mark meshes as clipped shapes for cross-section computation
  useEffect(() => {
    if (meshRef.current && useClipping) {
      meshRef.current.traverse((child) => {
        if (child.isMesh && child.geometry) {
          child.userData.isClippedShape = true
        }
      })
    }
  }, [useClipping])

  // Apply materials once
  useMemo(() => {
    if (useGradient) {
      applyGradientMaterial(clonedScene)
    } else if (materialType && MATERIALS[materialType]) {
      const planes = propClippingPlanes || (useClipping ? clippingPlanes : null)
      const materialConfig = teamColor 
        ? { ...MATERIALS[materialType], color: teamColor }
        : MATERIALS[materialType]
      applyMaterialToModel(clonedScene, materialConfig, planes)
    }
  }, [clonedScene, useGradient, materialType, useClipping, propClippingPlanes, teamColor])
  
  // Set renderOrder and outlines - only once
  useEffect(() => {
    if (clonedScene) {
      // Collect meshes first to avoid modifying during traversal
      const meshesToProcess = []
      clonedScene.traverse((child) => {
        if (child.isMesh && !child.userData.isSilhouette && !child.userData.hasOutline) {
          meshesToProcess.push(child)
        }
      })
      
      meshesToProcess.forEach((child) => {
        child.renderOrder = renderOrder
        
        if (showOutline) {
          if (outlineMode === 'wireframe') {
            // WIREFRAME MODE: Clean wireframe with proper depth testing
            const edges = new THREE.EdgesGeometry(child.geometry, 30)  // 30° angle for clean edges
            const lineMaterial = new THREE.LineBasicMaterial({ 
              color: outlineColor, 
              linewidth: 2,
              toneMapped: false,
              depthTest: true,  // Respect depth - don't draw over things in front
              depthWrite: false,
              transparent: true,
              opacity: 0.85
            })
            const line = new THREE.LineSegments(edges, lineMaterial)
            line.renderOrder = renderOrder + 1
            child.add(line)
            
            // Add thickness layers if needed
            if (outlineThickness > 1) {
              const thickness = 0.08
              const offsets = [
                [thickness, 0, 0],
                [-thickness, 0, 0],
                [0, thickness, 0],
                [0, -thickness, 0]
              ]
              for (let i = 0; i < Math.min(outlineThickness - 1, offsets.length); i++) {
                const extraLine = new THREE.LineSegments(edges, lineMaterial.clone())
                extraLine.position.set(offsets[i][0], offsets[i][1], offsets[i][2])
                extraLine.renderOrder = renderOrder + 1
                child.add(extraLine)
              }
            }
          } else if (outlineMode === 'silhouette') {
            // SILHOUETTE MODE: Inverted hull technique
            const silhouetteMaterial = new THREE.MeshBasicMaterial({
              color: outlineColor,
              side: THREE.BackSide,
              depthTest: true,
              depthWrite: false
            })
            const silhouetteMesh = new THREE.Mesh(child.geometry, silhouetteMaterial)
            silhouetteMesh.userData.isSilhouette = true
            const scale = 1.02 + (outlineThickness * 0.005)
            silhouetteMesh.scale.set(scale, scale, scale)
            silhouetteMesh.renderOrder = renderOrder - 1
            child.add(silhouetteMesh)
          } else {
            // EDGES MODE: Show geometry edges (default)
            const edges = new THREE.EdgesGeometry(child.geometry, outlineAngle)
            const lineMaterial = new THREE.LineBasicMaterial({ 
              color: outlineColor, 
              linewidth: outlineColor === '#ffffff' ? 3 : 4,
              toneMapped: false,
              depthTest: outlineColor !== '#ffffff',
              depthWrite: false
            })
            const line = new THREE.LineSegments(edges, lineMaterial)
            line.renderOrder = outlineColor === '#ffffff' ? 10000 : renderOrder + 1
            child.add(line)
            
            if (outlineThickness > 1) {
              for (let i = 1; i < outlineThickness; i++) {
                const extraLine = new THREE.LineSegments(edges, lineMaterial.clone())
                extraLine.renderOrder = line.renderOrder
                const extraScale = 1 + (i * 0.002)
                extraLine.scale.set(extraScale, extraScale, extraScale)
                child.add(extraLine)
              }
            }
          }
          child.userData.hasOutline = true
        }
      })
    }
  }, [clonedScene, renderOrder, showOutline, outlineColor, outlineAngle, outlineMode, outlineThickness])
  
  // Register geometry to global store for cross-section computation
  useEffect(() => {
    if (useClipping && groupRef.current) {
      const entry = { ref: groupRef, id: url }
      window.shapeGeometries.push(entry)
      
      return () => {
        const idx = window.shapeGeometries.findIndex(e => e.id === url && e.ref === groupRef)
        if (idx >= 0) window.shapeGeometries.splice(idx, 1)
      }
    }
  }, [useClipping, url])
  
  // Update gradient time
  useFrame((state) => {
    frameCount.current++
    
    if (meshRef.current && useGradient) {
      if (frameCount.current % FRAME_SKIP === 0) {
        // updateGradientTime if needed
      }
    }
  })
  
  return (
    <group ref={groupRef}>
      <primitive 
        ref={meshRef}
        object={clonedScene}
        frustumCulled={true}
        {...props}
      />
    </group>
  )
}

// ============================================
// ANIMATED SHAPE - Simple animation wrapper
// ============================================
function AnimatedShape({ shape, children }) {
  const meshRef = useRef()
  const animationDuration = 1500 // 1.5 seconds
  const isAnimatingRef = useRef(shape.isAnimating)
  
  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.traverse((child) => {
        if (child.isMesh) {
          child.renderOrder = 10000 
        }
      })
    }
  }, [])
  
  useFrame(() => {
    if (!meshRef.current || !isAnimatingRef.current) return
    
    const elapsed = Date.now() - shape.animationStartTime
    const progress = Math.min(elapsed / animationDuration, 1)
    
    if (progress < 1) {
      const easeProgress = 1 - Math.pow(1 - progress, 3)
      
      const newX = shape.entryPosition[0] + (shape.position[0] - shape.entryPosition[0]) * easeProgress
      const newY = shape.entryPosition[1] + (shape.position[1] - shape.entryPosition[1]) * easeProgress
      const newZ = shape.entryPosition[2] + (shape.position[2] - shape.entryPosition[2]) * easeProgress
      
      meshRef.current.position.set(newX, newY, newZ)
      meshRef.current.rotation.x += 0.02
      meshRef.current.rotation.y += 0.02
    } else {
      meshRef.current.position.set(...shape.position)
      shape.isAnimating = false
      isAnimatingRef.current = false
    }
  })
  
  return (
    <group ref={meshRef} position={shape.isAnimating ? shape.entryPosition : shape.position}>
      {children}
    </group>
  )
}

// PERFORMANCE FIX: Memoize Particles component
const Particles = React.memo(function Particles() {
  const particlesRef = useRef()
  const count = 200
  
  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 100
      pos[i * 3 + 1] = Math.random() * 50
      pos[i * 3 + 2] = (Math.random() - 0.5) * 100
    }
    return pos
  }, [])
  
  const frameCount = useRef(0)
  
  useFrame(() => {
    frameCount.current++
    if (frameCount.current % FRAME_SKIP !== 0) return
    
    if (particlesRef.current) {
      particlesRef.current.rotation.y += 0.0001
    }
  })
  
  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        color="#4ecdc4"
        size={0.2}
        sizeAttenuation={true}
        transparent
        opacity={0.6}
      />
    </points>
  )
})

export function Scene({ gameState, debugConfig = DEFAULT_DEBUG_CONFIG, setDebugConfig }) {
  const { camera } = useThree()
  
  // Calculate animation offsets for city elements
  const buildingOffset = 0.1
  const virtualBuildingOffset = 0.3
  const stadiumOffset = 0.15
  const proximityOffset = 0.2
  const hideOffset = 0.25
  const liftAmount = 0.1
  
  // Update clipping planes based on debug config
  const currentClippingPlanes = useMemo(() => {
    return createClippingPlanes(debugConfig.cagePosition, debugConfig.cageSize)
  }, [debugConfig.cagePosition, debugConfig.cageSize])
  
  return (
    <group>
      {/* Cross-section manager - computes at scene level like reference */}
      <CrossSectionManager 
        planes={currentClippingPlanes}
        crossSectionColor={debugConfig.crossSectionColor}
        camera={camera}
      />
      
      {/* DISABLED: Atmospheric particles
      <Particles />
      */}
      
      {/* City base models */}
      <Model url="/CITY_NY.gltf" materialType="newYork" renderOrder={0} castShadow receiveShadow />
      {/* CITY_TK with single wireframe outline - no thickness layers */}
      <Model 
        url="/CITY_TK.gltf" 
        materialType="tokyoOutline" 
        renderOrder={100} 
        showOutline={true} 
        outlineColor="#ffffff"
        outlineThickness={1}
        outlineMode="wireframe"
      />
      {/* DISABLED: City ground models
      <Model url="/CITY_NY-G.gltf" materialType="newYork" renderOrder={0} castShadow receiveShadow />
      <Model url="/CITY_T-G.gltf" materialType="tokyo" renderOrder={0} castShadow receiveShadow />
      */}
      
      <Model 
        url="/CITY_BUILDING.gltf" 
        materialType="newYork" 
        position={[-0.05, buildingOffset, -0.05]} 
        renderOrder={10} 
        showOutline={true} 
        outlineColor="#707070" 
        castShadow 
        receiveShadow 
      />
      
      <Model 
        url="/CITY_STADIUM.gltf" 
        materialType="stadium" 
        position={[1.2, stadiumOffset, -1.2]} 
        renderOrder={20} 
        castShadow 
        receiveShadow 
      />
      {/* Virtual Stadium - wireframe outline only (like Tokyo city) */}
      <Model 
        url="/CITY_STADIUM-VURTUAL.gltf" 
        materialType="tokyoOutline" 
        position={[-1.2, stadiumOffset + 0.25, 1.2]} 
        renderOrder={100} 
        showOutline={true} 
        outlineColor="#ffffff"
        outlineThickness={1}
        outlineMode="wireframe"
      />
      
      {/* Virtual Building - wireframe outline only (like Tokyo city) */}
      <Model 
        url="/CITY_BUILDING-VIRTUAL.gltf" 
        materialType="tokyoOutline" 
        position={[0.05, virtualBuildingOffset, 0.05]} 
        renderOrder={100} 
        showOutline={true} 
        outlineColor="#ffffff"
        outlineThickness={1}
        outlineMode="wireframe"
      />
      
      <Model 
        url="/CITY_PROX.gltf" 
        materialType="cityDark" 
        position={[0, proximityOffset, 0]} 
        renderOrder={50} 
        showOutline={true} 
        outlineColor="#707070"
        outlineThickness={3}
        castShadow 
        receiveShadow 
      />
      
      <Model 
        url="/CITY_HIDE.gltf" 
        useGradient={true} 
        position={[0, hideOffset - 1.0, 0]}
        scale={[1.2, 0.5, 1.2]}
        renderOrder={-10}
        castShadow={false}       
        receiveShadow={false}
      />
      
      {/* Wireframe cage for debugging */}
      <WireframeCage 
        visible={true} 
        position={debugConfig.cagePosition}
        size={debugConfig.cageSize}
        color={debugConfig.cageColor}
      />
      
      {gameState && gameState.placedShapes && gameState.placedShapes.map((shape) => {
        const shapeUrl = shape.team === 1 
          ? `/TEAM-A_TEAM-A-SHAPE-${(shape.shapeIndex % 12) + 1}.gltf`
          : `/TEAM-B_TEAM-B-SHAPE-${(shape.shapeIndex % 12) + 1}.gltf`
        
        return (
          <AnimatedShape 
            key={shape.id} 
            shape={shape}
          >
            <Model
              url={shapeUrl}
              materialType={shape.team === 1 ? 'teamA' : 'teamB'}
              position={[0, 0, 0]}
              rotation={shape.rotation}
              scale={debugConfig.shapeScale}
              renderOrder={10000}
              showOutline={false}
              useClipping={true}
              clippingPlanes={currentClippingPlanes}
              teamColor={shape.team === 1 ? debugConfig.teamAColor : debugConfig.teamBColor}
              castShadow
              receiveShadow
            />
          </AnimatedShape>
        )
      })}
    </group>
  )
}

useGLTF.preload('/CITY_BUILDING.gltf')
useGLTF.preload('/CITY_BUILDING-VIRTUAL.gltf')
useGLTF.preload('/CITY_DRONES-SIMPLE.gltf')
// useGLTF.preload('/CITY_GAME.gltf')
useGLTF.preload('/CITY_HIDE.gltf')
// City base models
useGLTF.preload('/CITY_NY.gltf')
useGLTF.preload('/CITY_TK.gltf')
// DISABLED: City ground models
// useGLTF.preload('/CITY_NY-G.gltf')
// useGLTF.preload('/CITY_T-G.gltf')
useGLTF.preload('/CITY_PROX.gltf')
useGLTF.preload('/CITY_ROOF.gltf')
useGLTF.preload('/CITY_STADIUM.gltf')
useGLTF.preload('/CITY_STADIUM-VURTUAL.gltf')

useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-1.gltf')
useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-2.gltf')
useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-3.gltf')
useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-4.gltf')
useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-5.gltf')
useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-6.gltf')
useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-7.gltf')
useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-8.gltf')
useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-9.gltf')
useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-10.gltf')
useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-11.gltf')
useGLTF.preload('/TEAM-A_TEAM-A-SHAPE-12.gltf')

useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-1.gltf')
useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-2.gltf')
useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-3.gltf')
useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-4.gltf')
useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-5.gltf')
useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-6.gltf')
useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-7.gltf')
useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-8.gltf')
useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-9.gltf')
useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-10.gltf')
useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-11.gltf')
useGLTF.preload('/TEAM-B_TEAM-B-SHAPE-12.gltf')

export default Scene
